/* oxlint-disable typescript/no-explicit-any -- schema library requires flexible any types for runtime type system */
/* oxlint-disable no-shadow-restricted-names -- constructor module name is intentional */
import type { StandardSchemaV1 } from '@standard-schema/spec'
import * as constructor from './constructor'
import type * as errors from './errors'
import type * as util from './util'

// Parse ctx
export interface ParseContext {
  error?: errors.ErrorMap
  async?: boolean
}

export interface ParsePayload<T = unknown> {
  value: T
  issues: errors.RawIssue[]
  aborted?: boolean
}

export type CheckFn<T> = (payload: ParsePayload<T>) => util.MaybeAsync<void>

// Base schema definition
export interface SchemaTypeDef {
  type: string
  error?: errors.ErrorMap | string | (() => string)
  checks?: Check[]
  meta?: Record<string, any>
  proto?: ProtoMetadata
  [key: string]: any // Allow extra properties for specific schema types
}

export type ProtoScalarType =
  | 'int32'
  | 'uint32'
  | 'sint32'
  | 'int64'
  | 'uint64'
  | 'sint64'
  | 'fixed64'
  | 'sfixed64'
  | 'float'
  | 'double'
  | 'bytes'
  | 'timestamp'

export type ProtoWireType =
  | 'varint'
  | 'fixed32'
  | 'fixed64'
  | 'length-delimited'

export interface ProtoScalarMetadata {
  type: ProtoScalarType
  wireType: ProtoWireType
}

export interface ProtoMetadata {
  field?: {
    tag: number
  }
  scalar?: ProtoScalarMetadata
}

// Check (validator) interface
export interface Check<T = any> {
  _zod: {
    def: {
      check: string
      error?: errors.ErrorMap | string | (() => string)
      abort?: boolean
    }
    check: CheckFn<T>
    onattach: ((schema: any) => void)[]
  }
}

// Base schema internals (hidden in _zod property)
export interface BaseSchemaInternals {
  def: SchemaTypeDef
  traits: Set<string>
  constr: new (def: any) => any
  deferred?: util.AnyFunc[]
  bag: Record<string, unknown>
  meta?: Record<string, any>
  output?: unknown
  input?: unknown

  objectShape?: () => Record<string, Schema>
  objectIsRecursive?: () => boolean

  // Parse and validation functions
  parse(payload: ParsePayload, ctx: ParseContext): util.MaybeAsync<ParsePayload>
  run(this: void, payload: ParsePayload, ctx: ParseContext): util.MaybeAsync<ParsePayload>
}

// Schema internals with typed input/output
export interface SchemaInternals<
  out O = unknown,
  out I = unknown,
> extends BaseSchemaInternals {
  output: O
  input: I
  optin?: 'optional' | 'defaulted'
  optout?: 'optional'
}

// Minimal schema type for constraints (allows circular references)
export type SomeSchema = { _zod: BaseSchemaInternals }

// Base schema interface
export interface Schema<
  O = unknown,
  I = unknown,
  Internals extends SchemaInternals<O, I> = SchemaInternals<O, I>,
> extends SomeSchema {
  _zod: Internals
  '~standard': StandardSchemaV1.Props<I, O>
  meta(metadata: Record<string, any>): this
  parse(data: unknown, params?: ParseContext): O
  safeParse(
    data: unknown,
    params?: ParseContext,
  ): { success: true; data: O } | { success: false; error: errors.SchemaError }
  describe(): SchemaDescriptor
  field(tag: number): this
  optional(): Schema<O | undefined, I | undefined>
  nullable(): Schema<O | null, I | null>
}

export interface SchemaDescriptor {
  type: string
  metadata?: Record<string, any>
  proto?: ProtoMetadata
  shape?: Record<string, SchemaDescriptor>
  element?: SchemaDescriptor
  key?: SchemaDescriptor
  value?: SchemaDescriptor
  values?: readonly string[]
  literal?: unknown
  options?: readonly SchemaDescriptor[]
  discriminator?: string
  schema?: SchemaDescriptor
  wrapper?: 'optional' | 'nullable' | 'default' | 'catch'
  default?: { hasValue: true; value?: unknown; factory?: true }
  catch?: { hasValue: true; value?: unknown; factory?: true }
}

// Type helpers for extracting input/output
export type input<T> = T extends { _zod: { input: any } }
  ? T['_zod']['input']
  : unknown
export type output<T> = T extends { _zod: { output: any } }
  ? T['_zod']['output']
  : unknown
export type infer<T> = output<T>

// Base constructor type
export const $constructor = constructor.$constructor
export type $constructor<
  T extends { _zod: any },
  D = any,
  // oxlint-disable-next-line typescript/no-unnecessary-type-arguments -- D allows callers to specify narrower def type
> = constructor.$constructor<T, D>

// Re-export utilities
export const NEVER = constructor.NEVER
export const AsyncError = constructor.AsyncError
